import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import useBranches from "hooks/useBranches";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE, API_HOST } from "config/api";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  churchName: PropTypes.string,
  departmentName: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

// Define individual Cell components with PropTypes
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

// Create a factory function for ChurchCell that receives the church object
const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
      <Box>
        <Typography variant="button" fontWeight={600} display="block">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {church?.email || "church@example.com"}
        </Typography>
      </Box>
    </Box>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const DepartmentCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </Typography>
  </Box>
);
DepartmentCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useDepartmentData() {
  const { user, church } = useAuth();
  const { branches, canFilterByBranch } = useBranches();
  const [branchFilter, setBranchFilter] = useState(null);

  // Create ChurchCell with access to the church object
  const ChurchCell = createChurchCell(church);

  const [departmentData, setDepartmentData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Church",
        accessor: "churchName",
        align: "left",
        width: "30%",
        Cell: ChurchCell,
      },
      {
        Header: "Department",
        accessor: "departmentName",
        align: "left",
        width: "25%",
        Cell: DepartmentCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "15%",
        Cell: StatusCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setEditModalOpen(true);
  };

  const handleDelete = (department) => {
    setSelectedDepartment(department);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedDepartment.id
        ? `${API_BASE}/departments/${selectedDepartment.id}`
        : `${API_BASE}/departments`;

      const method = selectedDepartment.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedDepartment.departmentName,
          status: selectedDepartment.status === "active",
          church_id: church.id,
          branch_id: selectedDepartment.branchId || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      Swal.fire({
        title: "Success!",
        text: `Department successfully saved`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchDepartments();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Department operation failed:", error);
      setEditModalOpen(false);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      //   setDepartmentData((prev) => ({
      //     ...prev,
      //     error: error.message,
      //   }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/departments/${selectedDepartment.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Department, ${selectedDepartment.departmentName} deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchDepartments();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      setDepartmentData((prev) => ({
        ...prev,
        error: error.message,
      }));
    }
  };

  const handleAddDepartment = () => {
    setSelectedDepartment({
      id: null,
      departmentName: "",
      status: "active",
      branchId: null,
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchDepartments = useCallback(async () => {
    try {
      setDepartmentData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      // Authenticated, branch-scoped endpoint — NOT /departments/church/{id},
      // which is the public unauthenticated route used by the registration
      // form and ignores branch scoping entirely.
      const url = branchFilter
        ? `${API_BASE}/departments?branch_id=${branchFilter}`
        : `${API_BASE}/departments`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const departments = result.data || result.departments || result;

      if (!Array.isArray(departments)) {
        throw new Error("API response is not an array");
      }

      setDepartmentData((prev) => ({
        ...prev,
        rows: departments.map((dept, index) => ({
          id: dept.id,
          serial: index + 1,
          churchName: church.name,
          departmentName: dept.name || dept.department_name,
          status: dept.status === "active" || dept.status === true ? "active" : "inactive",
          createdAt: dept.created_at,
          branchId: dept.branch_id,
          originalData: dept,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setDepartmentData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  }, [user, church, branchFilter]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  return {
    ...departmentData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedDepartment,
    setSelectedDepartment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddDepartment,
    branches,
    canFilterByBranch,
    branchFilter,
    setBranchFilter,
  };
}
